import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { AdminTitle, Field } from "@/components/admin/admin-ui";
import { Button } from "@/components/ui/button";
import { SupportActions } from "@/components/store/support-actions";
import { meta } from "@/components/store/page-parts";
import { useStore, type SupportSettings } from "@/lib/store-context";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/settings")({
  head: () => meta("Settings — BetterAccess Admin", "Manage storefront defaults, exchange rates, and operational preferences."),
  component: Settings,
});

function Settings() {
  const { support, setSupport } = useStore();
  const [storeName, setStoreName] = useState("BetterAccess Store");
  const [storeUrl, setStoreUrl] = useState("https://betteraccess.store");
  const [usdRate, setUsdRate] = useState(String(support.usd_to_bdt_rate || 122));
  const [storeNotice, setStoreNotice] = useState(
    support.store_notice || "⭐ আমাদের ওয়েবসাইটে এখন সম্পূর্ণভাবে ইনস্ট্যান্ট ডিজিটাল সাবস্ক্রিপশন ও অটোমেটেড সার্ভিস চালু হয়েছে! ⚡ Instant Automated Delivery in < 3s"
  );
  const [whatsapp, setWhatsapp] = useState(support.whatsapp || "880130244475");
  const [telegram, setTelegram] = useState(support.telegram || "https://t.me/betteraccess_support");
  const [email, setEmail] = useState(support.email || "support@betteraccess.store");
  const [brevoKey, setBrevoKey] = useState("");
  const [brevoSender, setBrevoSender] = useState("support@betteraccess.store");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/settings")
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          if (data.store_name) setStoreName(data.store_name);
          if (data.store_url) setStoreUrl(data.store_url);
          if (data.usd_to_bdt_rate) setUsdRate(String(data.usd_to_bdt_rate));
          if (data.store_notice) setStoreNotice(data.store_notice);
          if (data.support_whatsapp) setWhatsapp(data.support_whatsapp);
          if (data.support_telegram) setTelegram(data.support_telegram);
          if (data.support_email) setEmail(data.support_email);
          if (data.brevo_api_key) setBrevoKey(data.brevo_api_key);
          if (data.brevo_sender_email) setBrevoSender(data.brevo_sender_email);
        }
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    const payload = {
      store_name: storeName,
      store_url: storeUrl,
      usd_to_bdt_rate: usdRate,
      store_notice: storeNotice,
      support_whatsapp: whatsapp,
      support_telegram: telegram,
      support_email: email,
      brevo_api_key: brevoKey,
      brevo_sender_email: brevoSender,
    };

    try {
      const res = await fetch("/api/admin/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save settings");

      setSupport({
        ...support,
        whatsapp,
        telegram,
        email,
        usd_to_bdt_rate: Number(usdRate),
        store_notice: storeNotice,
      });

      toast.success("Store settings updated and synced to database!");
    } catch (err: any) {
      toast.error(err.message || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminTitle
        eyebrow="Configuration"
        title="Store settings"
        copy="Manage storefront identity, USD/BDT exchange rate, support channels and transactional email credentials."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="premium-panel p-5">
          <h2 className="font-black">Store identity &amp; Rates</h2>
          <div className="mt-5 grid gap-4">
            <Field label="Store name">
              <input
                value={storeName}
                onChange={e => setStoreName(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Store base URL">
              <input
                value={storeUrl}
                onChange={e => setStoreUrl(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="USD to BDT Exchange Rate (৳ per $1 USD)">
              <input
                type="number"
                value={usdRate}
                onChange={e => setUsdRate(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-mono font-bold"
              />
            </Field>
            <Field label="Storefront Notice Bar text">
              <textarea
                rows={2}
                value={storeNotice}
                onChange={e => setStoreNotice(e.target.value)}
                className="rounded-md border bg-background p-3 font-normal"
              />
            </Field>
          </div>
        </section>

        <section className="premium-panel p-5">
          <h2 className="font-black">Customer support</h2>
          <div className="mt-5 grid gap-4">
            <Field label="WhatsApp Number (e.g. 880130244475)">
              <input
                value={whatsapp}
                onChange={e => setWhatsapp(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Telegram Support Link">
              <input
                value={telegram}
                onChange={e => setTelegram(e.target.value)}
                placeholder="https://t.me/betteraccess_support"
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <Field label="Public Support Email">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
            <div>
              <p className="mb-2 text-xs font-bold">Support Actions Preview</p>
              <SupportActions compact />
            </div>
          </div>
        </section>

        <section className="premium-panel p-5 lg:col-span-2">
          <h2 className="font-black">Transactional Email (Brevo / SMTP)</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Used to send welcome emails, password reset OTPs, and order delivery credentials directly to customer inboxes.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Brevo API Key (xkeysib-...)">
              <input
                type="password"
                value={brevoKey}
                onChange={e => setBrevoKey(e.target.value)}
                placeholder="xkeysib-..."
                className="h-10 rounded-md border bg-background px-3 font-mono text-xs"
              />
            </Field>
            <Field label="Verified Sender Email">
              <input
                type="email"
                value={brevoSender}
                onChange={e => setBrevoSender(e.target.value)}
                placeholder="noreply@betteraccess.store"
                className="h-10 rounded-md border bg-background px-3 font-normal"
              />
            </Field>
          </div>
        </section>
      </div>

      <div className="mt-6 flex justify-end">
        <Button size="lg" className="font-bold" onClick={save} disabled={saving}>
          {saving ? (
            <span className="flex items-center gap-2">
              <RefreshCw className="size-4 animate-spin" /> Saving Settings...
            </span>
          ) : (
            "Save All Settings"
          )}
        </Button>
      </div>
    </>
  );
}